import React, { useState, useEffect } from "react";
import { getDataWAuth } from "../../utils/api-service";
import {
  IB_GET_REPORT,
  IB_GET_REPORT_FISMONTH,
} from "../../constants/constant";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import IBFilters from "../calculation/ibFilters";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid-pro";
import { Button } from "@deere/fuel-react";
import * as XLSX from "xlsx";
import { FileDownloadOutlined } from "@mui/icons-material";

const BankRateReport = () => {
  const [bankRates, setBankRates] = useState([]);
  const [fiscalMonths, setFiscalMonths] = useState([]);
  const [fiscalDateMap, setFiscalDateMap] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [defaultYear, setDefaultYear] = useState("");
  const [startFiscalMonth, setStartFiscalMonth] = useState("");
  const [endFiscalMonth, setEndFiscalMonth] = useState("");
  const [isViewReportActive, setIsViewReportActive] = useState(false);

  const exportValueFormatter = (value) => {
    if (value === null || value === "" || value === "N/A") return "-";
    else return Number(value).toFixed(2).replace(".", ",");
  };

  const handleExportXLSX = () => {
    const exportHeaders = [
      { label: "Rate Type", key: "rateType" },
      { label: "Currency", key: "currency" },
      ...fiscalMonths.map((monthYear, idx) => ({
        label: formattedFiscalMonths[idx] || "N/A",
        key: monthYear,
      })),
    ];

    const exportRows = rows.map((row) =>
      exportHeaders.reduce((acc, col) => {
        if (col.key !== "rateType" && col.key !== "currency") {
          acc[col.label] = exportValueFormatter(row[col.key]);
        } else {
          acc[col.label] = row[col.key];
        }
        return acc;
      }, {}),
    );

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(
      wb,
      `StandardRateReport_${startFiscalMonth}-${defaultYear}_${endFiscalMonth}-${defaultYear}.xlsx`,
    );
  };

  useEffect(() => {
    const fetchBankRates = async () => {
      try {
        const response = await getDataWAuth(IB_GET_REPORT);

        setDropdownOptions(response.data.listforDropdown || []);
        const combinedRates = [
          ...response.data.bankRatesList.map((rate) => ({
            id: rate.bankRateId,
            rateType: rate.rateType.ratetypes,
            currency: rate.rateType.currency,
            monthYear: rate.monthYear,
            rate: rate.rate,
            freeze: rate.freeze,
            type: "Bank Rate",
          })),
          ...response.data.customRatesList.map((rate) => ({
            id: rate.customRateid,
            rateType: `${rate.customRateType.country.countryName} - ${rate.customRateType.customRateType}`,
            currency: rate.customRateType.currency,
            monthYear: rate.fisMonthYear,
            rate: rate.rate,
            freeze: rate.customFreeze,
            type: "Custom Rate",
          })),
        ];

        setBankRates(combinedRates);

        const fiscalDateMap = response.data.fiscalDateDTOList.reduce(
          (acc, date) => {
            acc[date.fismonthyear] = {
              startDate: date.startDate,
              endDate: date.endDate,
            };
            return acc;
          },
          {},
        );
        setFiscalDateMap(fiscalDateMap);

        const fiscalMonths = response.data.fiscalDateDTOList.map(
          (date) => date.fismonthyear,
        );
        setFiscalMonths(fiscalMonths.sort());

        let startFiscalMonth = "";
        let endFiscalMonth = "";

        if (response.data.fiscalDateDTOList.length > 2) {
          startFiscalMonth =
            response.data.fiscalDateDTOList[2].fismonthyear.split("-")[0];
          endFiscalMonth =
            response.data.fiscalDateDTOList[0].fismonthyear.split("-")[0];
        }

        const newYears = [
          ...new Set(
            response.data.listforDropdown.map(
              (option) => option.fismonthyear.split("-")[1],
            ),
          ),
        ];

        setDefaultYear(newYears[0]);
        setStartFiscalMonth(startFiscalMonth);
        setEndFiscalMonth(endFiscalMonth);
      } catch (error) {
        console.error("Error fetching bank rates data:", error);
      }
    };

    fetchBankRates();
  }, []);

  const fetchBankRatesByFiscalMonth = async () => {
    try {
      const response = await getDataWAuth(
        `${IB_GET_REPORT_FISMONTH}?startFiscalMonth=${startFiscalMonth}-${defaultYear}&endFiscalMonth=${endFiscalMonth}-${defaultYear}`,
      );

      const combinedRates = [
        ...response.data.bankRatesList.map((rate) => ({
          id: rate.bankRateId,
          rateType: rate.rateType.ratetypes,
          currency: rate.rateType.currency,
          monthYear: rate.monthYear,
          rate: rate.rate,
          freeze: rate.freeze,
          type: "Bank Rate",
        })),
        ...response.data.customRatesList.map((rate) => ({
          id: rate.customRateid,

          rateType: `${rate.customRateType.country.countryName} - ${rate.customRateType.customRateType}`,
          currency: rate.customRateType.currency,
          monthYear: rate.fisMonthYear,
          rate: rate.rate,
          freeze: rate.customFreeze,
          type: "Custom Rate",
        })),
      ];

      setBankRates(combinedRates);

      const fiscalDateMap = response.data.fiscalDateDTOList.reduce(
        (acc, date) => {
          acc[date.fismonthyear] = {
            startDate: date.startDate,
            endDate: date.endDate,
          };
          return acc;
        },
        {},
      );
      setFiscalDateMap(fiscalDateMap);

      const fiscalMonths = response.data.fiscalDateDTOList.map(
        (date) => date.fismonthyear,
      );
      setFiscalMonths(fiscalMonths.sort());
    } catch (error) {
      console.error("Error fetching bank rates data:", error);
    } finally {
      setIsViewReportActive(false);
    }
  };

  if (bankRates.length === 0) {
    return <p>Loading data...</p>;
  }

  const formattedFiscalMonths = fiscalMonths.map((monthYear) => {
    const startDate = fiscalDateMap[monthYear]?.startDate;
    const endDate = fiscalDateMap[monthYear]?.endDate;

    if (startDate && endDate) {
      const formattedStartDate = dayjs(startDate).format("DD MMM YYYY");
      const formattedEndDate = dayjs(endDate).format("DD MMM YYYY");
      return `${formattedStartDate} to ${formattedEndDate}`;
    }
    return "N/A";
  });

  const columns = [
    {
      field: "rateType",
      headerName: "Rate Type",
      width: 200,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "currency",
      headerName: "Currency",
      width: 150,
      headerAlign: "right",
      align: "right",
    },
    ...fiscalMonths.map((monthYear, index) => ({
      field: monthYear,
      headerName: formattedFiscalMonths[index] || "N/A",
      width: 180,
      headerAlign: "right",
      align: "right",

      renderHeader: (params) => {
        const header = params.colDef.headerName;
        const formattedHeader = header.replace(" to ", " to\n");

        return (
          <div
            style={{
              whiteSpace: "pre-line",
              textAlign: "right",
              fontWeight: "bold",
            }}
          >
            {formattedHeader}{" "}
          </div>
        );
      },

      valueFormatter: (params) => {
        if (params === null || params === "" || params === "N/A") return "-";
        else return Number(params).toFixed(2) + " %";
      },
    })),
  ];

  const organizedRates = bankRates.reduce((acc, rate) => {
    if (!acc[rate.rateType]) {
      acc[rate.rateType] = { currency: rate.currency, rates: {} };
    }
    acc[rate.rateType].rates[rate.monthYear] = rate.rate;
    return acc;
  }, {});

  const handleYearChange = (e) => {
    setDefaultYear(e.target.value);
    setIsViewReportActive(true);
  };

  const handleStartDateChange = (e) => {
    setStartFiscalMonth(e.target.value);
    setIsViewReportActive(true);
  };

  const handleEndDateChange = (e) => {
    setEndFiscalMonth(e.target.value);
    setIsViewReportActive(true);
  };

  const rows = Object.keys(organizedRates)
    .map((rateType, index) => {
      const currency = organizedRates[rateType].currency;
      const rates = fiscalMonths.map(
        (monthYear) => organizedRates[rateType].rates[monthYear],
      );
      const allEmpty = rates.every(
        (rate) =>
          rate === null ||
          rate === "" ||
          rate === "N/A" ||
          rate === "-" ||
          rate === undefined,
      );

      if (allEmpty) return null;
      return {
        id: index,
        rateType: rateType,
        currency: currency,
        ...fiscalMonths.reduce((acc, monthYear) => {
          acc[monthYear] = organizedRates[rateType].rates[monthYear] || "N/A";
          return acc;
        }, {}),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.rateType.localeCompare(b.rateType));

  const gridToolbar = () => {
    return (
      <div>
        <GridToolbarContainer>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          {/* <GridToolbarExport printOptions={{ disableToolbarButton: true }} /> */}
          <Button
            color="inherit"
            size="small"
            startIcon={<FileDownloadOutlined style={{ color: "green" }} />}
            style={{
              boxShadow: "none",
              border: "none",
              background: "none",
              color: "green",
            }}
            onClick={handleExportXLSX}
          >
            Export
          </Button>
        </GridToolbarContainer>
      </div>
    );
  };

  return (
    <div style={{ paddingTop: "20px", height: "100%", width: "100%" }}>
      <IBFilters
        bankReport={true}
        defaultYear={defaultYear}
        startFiscalMonth={startFiscalMonth}
        endFiscalMonth={endFiscalMonth}
        dropdownOptions={dropdownOptions}
        fiscalDateDTOList={fiscalMonths}
        handleYearChange={handleYearChange}
        handleStartDateChange={handleStartDateChange}
        handleEndDateChange={handleEndDateChange}
        fetchBankRatesByFiscalMonth={fetchBankRatesByFiscalMonth}
        isViewReportActive={isViewReportActive}
      />
      <div
        style={{
          marginTop: "25px",
          borderRadius: "10px",
          backgroundColor: "white",
          height: "88%",
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={5}
          slots={{
            toolbar: gridToolbar,
          }}
          sx={{
            "& .MuiDataGrid-filler": {
              backgroundColor: "white",
            },

            "& .MuiDataGrid-columnHeader--sortable": {
              backgroundColor: "#fff",
            },
          }}
        />
      </div>
    </div>
  );
};

export default BankRateReport;
